/**
 * PortfolioKitHome — /portfolio-kit
 *
 * Landing page for Portfolio Kit (Product #3). Two variants served from one URL:
 *   MarketingVariant  — anonymous visitors / auth-hydration window
 *   WorkspaceVariant  — confirmed-authenticated customers
 *
 * Section order (MarketingVariant):
 *   1. Hero (exemplar button only — no RAG Agent archetype button)
 *   2. MetricsBand
 *   3. TrackAFeatureSection ("Portfolio Site Builder" wizard feature)
 *   4. PipelineSection (4-step flow diagram + "Start the wizard" CTA)
 *   5. ResourcesSection (flat 2-column, 5 items, exemplar first)
 *   6. ExemplarSection (Jordan)
 *   7. PricingCTA
 *
 * Section order (WorkspaceVariant):
 *   1. Welcome hero ("Welcome back, {name}")
 *   2. YourWorkTracker (unchanged)
 *   3. MetricsBand
 *   4. TrackAFeatureSection
 *   5. PipelineSection
 *   6. ResourcesSection
 *
 * Design system: coral accent via theme.palette.primary, Container maxWidth="lg",
 * Paper borderRadius: 3, hover translateY(-2px), staggered fade/slide-in.
 *
 * Per docs/PORTFOLIO_KIT_IA_AND_ROADMAP.md (Option C-refined, locked 2026-05-01):
 * same URL serves marketing content to anonymous visitors and workspace content
 * to logged-in customers.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WebIcon from "@mui/icons-material/Web";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CodeIcon from "@mui/icons-material/Code";
import ArticleIcon from "@mui/icons-material/Article";
import ChecklistIcon from "@mui/icons-material/Checklist";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PROJECT_ARCHETYPES,
  EXEMPLAR_PERSONA,
} from "../data/portfolioKit";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import YourWorkTracker from "../components/portfolio-kit/YourWorkTracker";
import ModulesStrip from "../components/portfolio-kit/ModulesStrip";
import MetricsBand from "../components/home/MetricsBand";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// ---------------------------------------------------------------------------
// Animation helpers
// ---------------------------------------------------------------------------

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: "fadeUp 0.5s ease both",
};

function withDelay(ms: number) {
  return { ...fadeUp, animationDelay: `${ms}ms` };
}

// ---------------------------------------------------------------------------
// Pipeline step data
// ---------------------------------------------------------------------------

const PIPELINE_STEPS = [
  {
    number: 1,
    labelKey: "PortfolioKitHome.pipelineStep1Label",
    Icon: AutoAwesomeIcon,
    descriptionKey: "PortfolioKitHome.pipelineStep1Description",
    emphasized: false,
  },
  {
    number: 2,
    labelKey: "PortfolioKitHome.pipelineStep2Label",
    Icon: BoltIcon,
    descriptionKey: "PortfolioKitHome.pipelineStep2Description",
    emphasized: true,
  },
  {
    number: 3,
    labelKey: "PortfolioKitHome.pipelineStep3Label",
    Icon: WebIcon,
    descriptionKey: "PortfolioKitHome.pipelineStep3Description",
    emphasized: false,
  },
  {
    number: 4,
    labelKey: "PortfolioKitHome.pipelineStep4Label",
    Icon: RocketLaunchIcon,
    descriptionKey: "PortfolioKitHome.pipelineStep4Description",
    emphasized: false,
  },
] as const;

// ---------------------------------------------------------------------------
// Resource data — exemplar walkthrough first
// ---------------------------------------------------------------------------

const RESOURCES = [
  {
    titleKey: "PortfolioKitHome.resourceExemplarTitle",
    route: "/portfolio-kit/demo",
    Icon: BoltIcon,
    descriptionKey: "PortfolioKitHome.resourceExemplarDescription",
  },
  {
    titleKey: "PortfolioKitHome.resourceArchetypesTitle",
    route: `/portfolio-kit/archetype/${PROJECT_ARCHETYPES[0].en.slug}`,
    Icon: CodeIcon,
    descriptionKey: "PortfolioKitHome.resourceArchetypesDescription",
  },
  {
    titleKey: "PortfolioKitHome.resourceReadmeTitle",
    route: "/portfolio-kit/readme-template",
    Icon: ArticleIcon,
    descriptionKey: "PortfolioKitHome.resourceReadmeDescription",
  },
  {
    titleKey: "PortfolioKitHome.resourceDeploymentTitle",
    route: "/portfolio-kit/deployment-checklist",
    Icon: ChecklistIcon,
    descriptionKey: "PortfolioKitHome.resourceDeploymentDescription",
  },
] as const;

// ---------------------------------------------------------------------------
// Shared: TrackAFeatureSection
// ---------------------------------------------------------------------------

function TrackAFeatureSection({ delay = 180 }: { delay?: number }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(delay) }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: `1.5px solid ${theme.palette.primary.main}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <WebIcon sx={{ color: theme.palette.primary.main }} />
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}
          >
            {t("PortfolioKitHome.siteBuilderEyebrow")}
          </Typography>
        </Stack>
        <Typography
          variant="h4"
          fontWeight={500}
          sx={{ letterSpacing: "-0.02em", mb: 1.5 }}
        >
          {t("PortfolioKitHome.siteBuilderTitle")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 2.5, maxWidth: 780, lineHeight: 1.6 }}
        >
          {t("PortfolioKitHome.siteBuilderBody")}
        </Typography>
        <Stack spacing={1} mb={3}>
          {[
            t("PortfolioKitHome.siteBuilderBullet1"),
            t("PortfolioKitHome.siteBuilderBullet2"),
            t("PortfolioKitHome.siteBuilderBullet3"),
            t("PortfolioKitHome.siteBuilderBullet4"),
          ].map((bullet) => (
            <Stack key={bullet} direction="row" spacing={1} alignItems="center">
              <BoltIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
              <Typography variant="body2">{bullet}</Typography>
            </Stack>
          ))}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/portfolio-kit/lovable-prompt")}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              px: 3,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {t("PortfolioKitHome.runWizardButton")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/portfolio-kit/demo")}
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.5),
              color: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            {t("PortfolioKitHome.seeDemoButton")}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Shared: PipelineSection (4-step flow diagram + CTA)
// ---------------------------------------------------------------------------

function PipelineStep({
  step,
}: {
  step: (typeof PIPELINE_STEPS)[number];
}) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const { number, labelKey, Icon, descriptionKey, emphasized } = step;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: emphasized
          ? `1.5px solid ${theme.palette.primary.main}`
          : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        bgcolor: emphasized ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
        flex: 1,
        minWidth: 0,
        py: emphasized ? { xs: 3, md: 3.5 } : { xs: 2, md: 2.5 },
      }}
    >
      {/* Step number circle */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: emphasized ? theme.palette.primary.main : "transparent",
          border: emphasized ? "none" : `2px solid ${theme.palette.primary.main}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: emphasized ? theme.palette.background.paper : theme.palette.primary.main, lineHeight: 1 }}
        >
          {number}
        </Typography>
      </Box>

      {/* Icon */}
      <Icon sx={{ fontSize: 28, color: theme.palette.primary.main, mb: 1 }} />

      {/* Title */}
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{ mb: 0.75, lineHeight: 1.2 }}
      >
        {t(labelKey)}
      </Typography>

      {/* Description */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.5 }}
      >
        {t(descriptionKey)}
      </Typography>
    </Box>
  );
}

function PipelineSection({ delay = 260 }: { delay?: number }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(delay) }}>
      <Typography
        variant="overline"
        fontWeight={700}
        sx={{
          color: theme.palette.primary.main,
          letterSpacing: "0.08em",
          display: "block",
          mb: 1,
        }}
      >
        {t("PortfolioKitHome.howItWorksEyebrow")}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={500}
        sx={{ letterSpacing: "-0.02em", mb: 4 }}
      >
        {t("PortfolioKitHome.howItWorksTitle")}
      </Typography>

      {/* Pipeline row — horizontal on md+, stacked on xs */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 0 },
        }}
      >
        {PIPELINE_STEPS.map((step, idx) => (
          <Box
            key={step.number}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            <PipelineStep step={step} />

            {/* Arrow between steps — only on md+ */}
            {idx < PIPELINE_STEPS.length - 1 && (
              <ArrowForwardIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 20,
                  flexShrink: 0,
                  mx: { xs: 0, md: 1 },
                  my: { xs: 0.5, md: 0 },
                  display: { xs: "none", md: "block" },
                }}
              />
            )}
          </Box>
        ))}

        {/* CTA at the end of the pipeline */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            ml: { xs: 0, md: 2 },
            mt: { xs: 1, md: 0 },
          }}
        >
          <ArrowForwardIcon
            sx={{
              color: theme.palette.primary.main,
              fontSize: 20,
              flexShrink: 0,
              display: { xs: "none", md: "block" },
            }}
          />
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/portfolio-kit/lovable-prompt")}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {t("PortfolioKitHome.startWizardButton")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Shared: ResourcesSection (flat 2-column grid, exemplar first)
// ---------------------------------------------------------------------------

function ResourceCard({
  resource,
}: {
  resource: (typeof RESOURCES)[number];
}) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const { titleKey, route, Icon, descriptionKey } = resource;

  return (
    <Paper
      elevation={0}
      onClick={() => navigate(route)}
      sx={{
        p: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 3,
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Icon sx={{ color: theme.palette.primary.main, mb: 1.5, fontSize: 26 }} />
      <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
        {t(titleKey)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {t(descriptionKey)}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ color: theme.palette.primary.main, mt: 2 }}
      >
        <Typography variant="body2" fontWeight={500}>
          {t("PortfolioKitHome.openLabel")}
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 16 }} />
      </Stack>
    </Paper>
  );
}

function ResourcesSection({ delay = 340 }: { delay?: number }) {
  const { t } = useTranslation("pages-products");
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(delay) }}>
      <Typography
        variant="h4"
        fontWeight={500}
        sx={{ letterSpacing: "-0.02em", mb: 4 }}
      >
        {t("PortfolioKitHome.resourcesTitle")}
      </Typography>

      {/* Flat 2-column card grid — exemplar walkthrough first */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {RESOURCES.map((resource) => (
          <ResourceCard
            key={resource.route + resource.titleKey}
            resource={resource}
          />
        ))}
      </Box>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Marketing-only: ExemplarSection (Jordan)
// ---------------------------------------------------------------------------

function ExemplarSection() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const persona = L(EXEMPLAR_PERSONA, i18n.language);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(400) }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
          color: theme.palette.background.default,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="flex-start"
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}
            >
              {t("PortfolioKitHome.exemplarEyebrow")}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={500}
              sx={{ mt: 1, mb: 1, color: theme.palette.background.default }}
            >
              {persona.name}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={500}
              sx={{ color: alpha(theme.palette.background.default, 0.75), mb: 2 }}
            >
              {persona.title} · {persona.focus}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: alpha(theme.palette.background.default, 0.85), mb: 3, lineHeight: 1.6 }}
            >
              {persona.positioning}
            </Typography>
            <Stack spacing={1} mb={3}>
              {persona.exemplarProjects.map((p) => (
                <Stack key={p} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleIcon
                    sx={{ fontSize: 18, color: theme.palette.primary.main, mt: 0.3 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(theme.palette.background.default, 0.85) }}
                  >
                    {p}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/portfolio-kit/demo")}
              sx={{
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
              }}
            >
              {t("PortfolioKitHome.exploreJordansPortfolioButton")}
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              fontWeight={700}
              sx={{
                color: alpha(theme.palette.background.default, 0.55),
                letterSpacing: "0.08em",
              }}
            >
              {t("PortfolioKitHome.skillsMatrixEyebrow")}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: theme.palette.success.light, mb: 0.5 }}
              >
                {t("PortfolioKitHome.strongSkillsLabel")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.background.default, 0.75),
                  display: "block",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {persona.strongSkills.join(" · ")}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: theme.palette.warning.main, mb: 0.5 }}
              >
                {t("PortfolioKitHome.moderateSkillsLabel")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.background.default, 0.75),
                  display: "block",
                  mb: 2,
                  lineHeight: 1.6,
                }}
              >
                {persona.moderateSkills.join(" · ")}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: alpha(theme.palette.background.default, 0.55), mb: 0.5 }}
              >
                {t("PortfolioKitHome.gapsInProgressLabel")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.background.default, 0.6),
                  display: "block",
                  lineHeight: 1.6,
                }}
              >
                {persona.gapsInProgress.join(" · ")}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Marketing-only: PricingCTA
// ---------------------------------------------------------------------------

function PricingCTA() {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, ...withDelay(450) }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" fontWeight={500} mb={1}>
          {t("PortfolioKitHome.pricingCtaTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          {t("PortfolioKitHome.pricingCtaBody")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
        >
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/pricing")}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              px: 4,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {t("PortfolioKitHome.seeAllAccessPricingButton")}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/visibility-strategy")}
            sx={{
              fontWeight: 500,
              px: 4,
              borderColor: alpha(theme.palette.primary.main, 0.5),
              color: theme.palette.primary.main,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            {t("PortfolioKitHome.startVisibilityWorkshopButton")}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// MarketingVariant — anonymous-state view of /portfolio-kit
//
// Rendered to anonymous visitors AND during the auth-hydration window for
// not-yet-confirmed-authenticated users. The top-level PortfolioKitHome
// dispatcher routes between this and WorkspaceVariant based on auth state.
//
// Per docs/PORTFOLIO_KIT_IA_AND_ROADMAP.md (Option C-refined, locked
// 2026-05-01).
// ---------------------------------------------------------------------------

function MarketingVariant() {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* 1. Hero — exemplar button only; no "Start with RAG Agent archetype" */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 6, md: 8 },
          ...withDelay(0),
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip
              label={t("PortfolioKitHome.marketingHeroEyebrow")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h2"
              fontWeight={500}
              sx={{
                letterSpacing: "-0.03em",
                fontSize: { xs: "2.4rem", md: "3.2rem" },
                maxWidth: 900,
              }}
            >
              {t("PortfolioKitHome.marketingHeroTitleLine1")}
              <br />
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                {t("PortfolioKitHome.marketingHeroTitleLine2")}
              </Box>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}
            >
              {t("PortfolioKitHome.marketingHeroSubheading")}
            </Typography>
            {/* Single CTA: exemplar site only */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/portfolio-kit/demo")}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  px: 3,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {t("PortfolioKitHome.seeExemplarSiteButton")}
              </Button>
            </Stack>
            {/* Free-feature checkmarks */}
            <Stack direction="row" spacing={3} mt={3} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("PortfolioKitHome.checkReadmeFree")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("PortfolioKitHome.checkArchetypesMapped")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("PortfolioKitHome.checkWizardBuildsPrompt")}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* 2. MetricsBand */}
      <MetricsBand sx={withDelay(100)} />

      {/* 3. Track A wizard feature */}
      <TrackAFeatureSection delay={180} />

      {/* 4. Pipeline flow diagram */}
      <PipelineSection delay={260} />

      {/* 5. Resources list */}
      <ResourcesSection delay={340} />

      {/* 6. Jordan exemplar */}
      <ExemplarSection />

      {/* 7. Pricing CTA */}
      <PricingCTA />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// WorkspaceVariant — logged-in-state view of /portfolio-kit
//
// Phase A v1 per docs/PORTFOLIO_KIT_IA_AND_ROADMAP.md §8 Phase A.
// Welcomes the customer, shows YourWorkTracker, then adds the Pipeline
// design sections (MetricsBand, Track A feature, flow diagram, resources).
// No Jordan exemplar block or pricing CTA — logged-in customers don't need
// the marketing paywall; the "Exemplar walkthrough" resource card already
// links to the demo.
// ---------------------------------------------------------------------------

function WorkspaceVariant() {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const { user } = useAuth();
  const greetingName =
    user?.display_name?.split(" ")[0] || t("PortfolioKitHome.greetingFallbackName");

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* 1. Welcome hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip
              label={t("PortfolioKitHome.workspaceHeroEyebrow")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.06em",
                bgcolor: theme.palette.primary.main,
                color: theme.palette.background.paper,
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
              {t("PortfolioKitHome.welcomeBackTitle", { name: greetingName })}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", maxWidth: 720, fontWeight: 400 }}
            >
              {t("PortfolioKitHome.workspaceHeroBody")}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* 2. YourWorkTracker — Phase B2 surface; empty-state on day one */}
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        <YourWorkTracker />
      </Container>

      {/* 3. ModulesStrip — Phase C curriculum modules */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <ModulesStrip />
      </Container>

      {/* 4. MetricsBand */}
      <MetricsBand sx={withDelay(0)} />

      {/* 4. Track A wizard feature */}
      <TrackAFeatureSection delay={0} />

      {/* 5. Pipeline flow diagram */}
      <PipelineSection delay={0} />

      {/* 6. Resources list */}
      <ResourcesSection delay={0} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PortfolioKitHome — top-level auth-aware dispatcher for /portfolio-kit
//
// Loading-state strategy: while auth is hydrating (loading=true) OR the
// user is anonymous, render MarketingVariant. Only confirmed-authenticated
// users see WorkspaceVariant. This avoids a flash-of-marketing for paying
// customers — naively gating on isAuthenticated alone would render
// MarketingVariant during the ~100-500ms loading window because
// isAuthenticated is false until /auth/me resolves.
//
// Pattern matches Dashboard.tsx and CareerTransitionHome.tsx.
// ---------------------------------------------------------------------------

export default function PortfolioKitHome() {
  const { isAuthenticated, loading } = useAuth();

  if (loading || !isAuthenticated) {
    return <MarketingVariant />;
  }
  return <WorkspaceVariant />;
}
